// TYPES --------------------

import { EvaluationContext, FlagValue } from '@openfeature/web-sdk'

export const LOCAL_FLAGS_AUTHORIZATION_KEY = 'localhost'

export enum FeatureFlagProvider {
  LAUNCH_DARKLY = 'launchDarkly',
  LOCAL = 'local',
}

export enum TrafficType {
  THYMEBOX_USER = 'user', // Thyme Box only
  REALTHYME_PRACTICE = 'practice', // RealThyme only
  REALTHYME_USER = 'practice_user', // RealThyme only
  CONNECT_USER_SESSION = 'user-session', // Connect only
}

export type FeatureFlagProviderConfig = {
  primaryProvider: FeatureFlagProvider
  launchDarklyClientSideId: string
}

export type FeatureFlagTargetingConfig = {
  // If undefined, OpenFeature will generate a random targeting key which will persist for the session.
  // https://launchdarkly.com/docs/home/flags/anonymous-contexts#store-anonymous-keys-locally
  targetingKey: string | undefined
  trafficType: TrafficType
  attributes?: EvaluationContext
}

/**
 * Non-object flags must always have a defined value.
 * Objects allow undefined to make handling empty objects easier.
 */
type ObjectsOptional<T> = T extends object ? T | undefined : T

export type FlagDefinition<T> = {
  /** The value the client should provide when the app is running locally. */
  local: ObjectsOptional<T>
  /**
   * The value this flag should have when no remote value is available.
   *
   * Used when:
   * 1) Remote SDK has not been initialized yet
   * 2) An error occurs
   */
  fallback: ObjectsOptional<T>
}

export type FlagDefinitions = Record<
  string,
  // Define these explicitly so that we can enforce that local and fallback values
  // are the same type
  | FlagDefinition<boolean>
  | FlagDefinition<string>
  | FlagDefinition<number>
  | FlagDefinition<object>
>

/** A property on FlagDefinitions, i.e. a flag name */
export type FlagKey<T extends FlagDefinitions> = string & keyof T

/**
 * Type helper which resolves to the Flag's type parameter, i.e. Flag<boolean>
 * => boolean
 */
type CalculatedFlagValue<T extends FlagDefinition<any>> =
  T extends FlagDefinition<infer U> ? ObjectsOptional<U> : never

/**
 * An object with key-value pairs for each feature flag specified in the
 * FeatureFlagDefinition.
 *
 * E.g:
 * {
 *   showHelloWorld: boolean,
 *   someStringFlag: string,
 * }
 */
export type FeatureFlagValues<T extends FlagDefinitions> = {
  [Properties in FlagKey<T>]: CalculatedFlagValue<T[Properties]>
}

// Define Flag type based on OpenFeature spec since it's not exported
export type OpenFeatureFlag<T extends FlagValue> = {
  variants: Record<string, T>
  disabled: boolean
  defaultVariant: string
}
